import palette from 'src/designSystem/variables/palette'
import styled, { css, keyframes } from 'styled-components/macro'
import { ReactComponent as EroIcon } from 'src/components/Icons/Brand/ero_dark_east.svg'
import { ChevronIcon } from 'src/components/Reusable/Buttons/PopupButton/PopupButton.styles'
import { FlexRow } from 'src/components/Reusable/Flex'
import { DownloadSVG } from 'src/components/Icons/Basic'

export const ChatConversationPanelContainer = styled.div<{
  $isAgentChat?: boolean
  $isArtifactPanelOpen?: boolean
  $noBottomSpace?: boolean
  $isCopilotMode?: boolean
}>`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: ${({ $isCopilotMode }) =>
    $isCopilotMode ? '8px 0 48px' : '16px 0 48px'};
  ${({ $noBottomSpace }) =>
    $noBottomSpace &&
    `
    padding-bottom: 0;
  `}
  overflow: auto;
  flex: 1;
  position: relative;
  pointer-events: auto;

  ${({ $isAgentChat, $isArtifactPanelOpen, $isCopilotMode }) =>
    $isAgentChat &&
    !$isCopilotMode &&
    !$isArtifactPanelOpen &&
    `
    width: 100%;
    align-items: center;
  `}
`

export const ChatConversationContainer = styled.div<{
  $isAgentChat?: boolean
  $isArtifactPanelOpen?: boolean
  $noBottomSpace?: boolean
  $isCopilotMode?: boolean
}>`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  margin-bottom: ${({ $noBottomSpace }) => ($noBottomSpace ? '0' : '24px')};
  gap: 16px;
  padding-right: ${({ $isCopilotMode }) => ($isCopilotMode ? '8px' : '16px')};
  pointer-events: auto;

  ${({ $isAgentChat, $isArtifactPanelOpen, $isCopilotMode }) =>
    $isAgentChat &&
    !$isCopilotMode &&
    !$isArtifactPanelOpen &&
    `
    width: 880px;
    max-width: 900px;
    margin: 0 auto;
  `}
`

const highlightPulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(255, 235, 59, 0.6);
  }
  50% {
    box-shadow: 0 0 0 8px rgba(255, 235, 59, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(255, 235, 59, 0);
  }
`

export const ChatUserMessageContainer = styled.div<{
  $isAgentChat?: boolean
  $isHighlighted?: boolean
}>`
  display: inline-flex;
  flex-direction: row;
  gap: ${({ $isAgentChat }) => ($isAgentChat ? '4px' : '16px')};
  padding: ${({ $isAgentChat }) => ($isAgentChat ? '4px' : '24px')};
  border-radius: 6px;
  max-width: 80%;
  margin-left: auto;
  transition: background-color 0.5s ease;

  ${({ $isHighlighted }) =>
    $isHighlighted &&
    css`
      background-color: rgba(255, 235, 59, 0.2);
      animation: ${highlightPulse} 1.5s ease-out;
    `}
`

export const DebugMessageContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-right: auto;
`

export const DebugMessageContentContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 32px;
  align-items: center;
  padding: 0 4px;
`

export const DebugMessageContent = styled.div`
  display: flex;
  border: 1px solid ${palette.neutral[100]};
  border-radius: 8px;
  padding: 12px;
  background-color: ${palette.neutral[50]};
  max-height: 400px;
  overflow: auto;
  width: 100%;
  margin-top: 8px;
`

export const PlaybookPromptContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-right: 30px;
`

export const ChatAgentMessageContainer = styled.div<{
  $isAgentChat?: boolean
  $isHighlighted?: boolean
}>`
  display: flex;
  flex-direction: ${({ $isAgentChat }) => ($isAgentChat ? 'column' : 'row')};
  gap: 8px;
  padding: ${({ $isAgentChat }) => ($isAgentChat ? '4px' : '24px')};
  border-radius: 6px;
  ${({ $isAgentChat }) =>
    !$isAgentChat &&
    `
    background-color: ${palette.neutral[30]};
  `}
  justify-content: 'flex-start';
  transition: background-color 0.5s ease;

  ${({ $isHighlighted, $isAgentChat }) =>
    $isHighlighted &&
    css`
      background-color: ${$isAgentChat
        ? 'rgba(255, 235, 59, 0.2)'
        : 'rgba(255, 235, 59, 0.25)'};
      animation: ${highlightPulse} 1.5s ease-out;
    `}
`

export const ChatEroIcon = styled(EroIcon)`
  width: 32px;
  height: 32px;
  flex-shrink: 0;
`

const MessageText = css`
  display: flex;
  font-size: 14px;
  font-weight: 400;
  line-height: 24px;
  color: ${palette.neutral[900]};
  word-break: break-word;
`

export const UserMessageText = styled.div<{
  $isAgentChat?: boolean
}>`
  ${MessageText}
  text-align: left;
  justify-content: right;
  white-space: pre-wrap;

  ${({ $isAgentChat }) =>
    $isAgentChat &&
    `
      flex-direction: column;
      gap: 8px;
      background: ${palette.purple[50]};
      padding: 16px;
      border-radius: 20px;
      position: relative;
      top: 4px;

       &::after {
        content: "";
        position: absolute;
        top: 0;
        right: 0;
        width: 0;
        height: 0;
        border-left: 8px solid ${palette.purple[50]};
        border-right: 8px solid ${palette.purple[50]};
        border-top: 8px solid ${palette.purple[50]};
        border-bottom: 8px solid ${palette.purple[50]};
      }
    `}
`

export const AgentMessageText = styled.div<{
  $isThinking?: boolean
  $isAgentChat?: boolean
  $isLeadPrompt?: boolean
  $isDebugMessage?: boolean
}>`
  ${MessageText}
  flex-direction: ${({ $isThinking }) => ($isThinking ? 'row' : 'column')};
  flex-grow: ${({ $isDebugMessage }) => ($isDebugMessage ? 0 : 1)};
  text-align: 'left';
  justify-content: left;

  font-family: Poppins;
  font-style: Regular;
  leading-trim: NONE;
  letter-spacing: 0.5px;
  line-height: 30px;

  ${({ $isThinking }) =>
    $isThinking &&
    `
    align-items: center;
  `}

  ${({ $isLeadPrompt }) =>
    $isLeadPrompt &&
    `
    flex-grow: 0;
    color: #005BDA;
  `}
`

const bounce = keyframes`
  0%, 100% { opacity: 0.3; };
  50% { opacity: 1; };
`

export const Dots = styled.div`
  display: flex;
  align-items: center;
  padding: 8px;
  width: 50px;
`

export const Dot = styled.span<{ delay: number }>`
  width: 8px;
  height: 8px;
  margin: 0 2px;
  background-color: ${palette.neutral[100]};
  border-radius: 50%;
  animation: ${bounce} 1s infinite;
  animation-delay: ${({ delay }) => delay}s;
`

export const ChatActionIconsContainer = styled.div<{ $isAgentChat?: boolean }>`
  display: flex;
  flex-direction: ${({ $isAgentChat }) => ($isAgentChat ? 'row' : 'column')};
  gap: 8px;
`

export const ChatActionIconsRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  margin-top: 8px;
  margin-bottom: 4px;
`

export const ChatArtifactContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

export const ChatContextModifiedContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  font-size: 14px;
  font-weight: 400;
  line-height: 21px;
  margin: 16px 0;
  padding: 8px 16px;
  gap: 8px;
  border-radius: 6px;
  border: 1px solid ${palette.neutral[100]};
  color: ${palette.neutral[600]};
`

export const StatusDisplay = styled.div<{ $isVisible?: boolean }>`
  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
  transition: opacity 0.3s ease-in-out;
  min-height: 1.5em;
  font-size: 12px;
  font-weight: 400;
  font-style: italic;
  color: ${palette.neutral[600]};
`

export const ConfirmationButton = styled.button<{
  $disabled?: boolean
}>`
  border: 1px solid ${palette.neutral[100]};
  border-radius: 12px;
  background-color: ${palette.neutral[30]};
  padding: 0px 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 400;
  line-height: 24px;
  color: ${palette.neutral[900]};
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background-color: ${palette.neutral[40]};
    border-color: ${palette.neutral[200]};
  }

  &:active:not(:disabled) {
    background-color: ${palette.neutral[50]};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`

export const ArrowDownSVG = styled(ChevronIcon)`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  padding: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  pointer-events: auto;
  background: transparent;
  path {
    fill: ${palette.neutral.white};
  }
  flex-shrink: 0;
`

export const MoreConversationArrowContainer = styled(FlexRow)`
  width: 50px;
  height: 50px;
  cursor: pointer;
  padding: 10px 20px;
  background: linear-gradient(148.34deg, #847dd3 14%, #7b46cb 94.04%);
  border: 1px solid #7b46cb;
  opacity: 0.3;
  max-width: 70px;
  border-radius: 50%;
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  bottom: 0px;
  &:hover {
    border: none;
    opacity: 1;
  }
`

export const DownloadFileIcon = styled(DownloadSVG)`
  cursor: pointer;
  svg {
    flex-shrink: 0;
  }
`
